import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { pickText, type Locale, type LocalizedText } from "@/domain/localized";
import { useApp } from "../container";
import { useSettings } from "../hooks/useSettings";
import { detectLocale } from "./detect";
import { strings, type Strings } from "./strings";

export interface LocaleValue {
  locale: Locale;
  t: Strings;
  pick: (text: LocalizedText) => string;
  /** `undefined` follows the device language again. */
  setLocale: (locale: Locale | undefined) => Promise<void>;
}

const LocaleContext = createContext<LocaleValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const app = useApp();
  const settings = useSettings();
  const locale: Locale = settings.data?.locale ?? detectLocale();

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleValue>(
    () => ({
      locale,
      t: strings[locale],
      pick: (text) => pickText(text, locale),
      setLocale: async (next) => {
        app.analytics.track("locale_set", { locale: next ?? "system" });
        await app.store.settings.update({ locale: next });
      },
    }),
    [locale, app],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("LocaleContext is missing; wrap the tree in <AppProviders>");
  return value;
}
